import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Container,
  Button,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Home as HomeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Notes as NotesIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { PropertyBlurbPrompt } from '@horwath-htl/shared-types';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableRowProps {
  prompt: PropertyBlurbPrompt;
  onEdit: (prompt: PropertyBlurbPrompt) => void;
  onDelete: (prompt: PropertyBlurbPrompt) => void;
  disabled?: boolean;
}

const SortableRow: React.FC<SortableRowProps> = ({ prompt, onEdit, onDelete, disabled = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: prompt.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : disabled ? 0.6 : 1,
    backgroundColor: isDragging ? '#f5f5f5' : 'transparent',
  };

  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell
        sx={{
          width: 50,
          cursor: disabled ? 'not-allowed' : 'grab',
          opacity: disabled ? 0.5 : 1
        }}
        {...attributes}
        {...(disabled ? {} : listeners)}
      >
        <DragIndicatorIcon color={disabled ? 'disabled' : 'action'} />
      </TableCell>
      <TableCell sx={{ width: 90 }}>{prompt.process_order}</TableCell>
      <TableCell sx={{ minWidth: 250 }}>{prompt.name}</TableCell>
      <TableCell sx={{ width: 180 }}>{prompt.id}</TableCell>
      <TableCell sx={{ minWidth: 300, maxWidth: 400 }}>
        <Tooltip title={prompt.prompt}>
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {prompt.prompt}
          </Box>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ width: 150 }}>
        {new Date(prompt.created_at).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
      </TableCell>
      <TableCell sx={{ width: 120 }}>
        <IconButton size="small" onClick={() => onEdit(prompt)} color="primary">
          <EditIcon />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete(prompt)} color="error">
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export const PropertyBlurbPromptsPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<PropertyBlurbPrompt | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    prompt: '',
    good_example: '',
    bad_example: '',
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Local prompts state for drag-and-drop
  const [prompts, setPrompts] = useState<PropertyBlurbPrompt[]>([]);

  // Fetch prompts
  const { data, isLoading } = useQuery({
    queryKey: ['property-blurb-prompts'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/deliverables/property-blurbs/prompts');
      if (!response?.ok) throw new Error('Failed to fetch prompts');
      return await response.json();
    },
  });

  // Update local prompts state when data changes
  useEffect(() => {
    if (data?.data) {
      setPrompts(data.data);
    }
  }, [data]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await authenticatedFetch('/api/deliverables/property-blurbs/prompts/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response) {
        throw new Error('No response from server');
      }
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create prompt');
      }
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-blurb-prompts'] });
      setCreateDialogOpen(false);
      resetForm();
      setSnackbar({
        open: true,
        message: 'Prompt created successfully',
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await authenticatedFetch(`/api/deliverables/property-blurbs/prompts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response) {
        throw new Error('No response from server');
      }
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update prompt');
      }
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-blurb-prompts'] });
      setEditDialogOpen(false);
      setSelectedPrompt(null);
      resetForm();
      setSnackbar({
        open: true,
        message: 'Prompt updated successfully',
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await authenticatedFetch(
        `/api/deliverables/property-blurbs/prompts/${id}/delete`,
        {
          method: 'DELETE',
        }
      );
      if (!response) {
        throw new Error('No response from server');
      }
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete prompt');
      }
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-blurb-prompts'] });
      setDeleteDialogOpen(false);
      setSelectedPrompt(null);
      setSnackbar({
        open: true,
        message: 'Prompt deleted successfully',
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    },
  });

  // Reorder mutation
  const reorderMutation = useMutation({
    mutationFn: async (reorderedPrompts: PropertyBlurbPrompt[]) => {
      const reorderData = reorderedPrompts.map((prompt, index) => ({
        id: prompt.id,
        process_order: index + 1,
      }));

      const response = await authenticatedFetch(
        '/api/deliverables/property-blurbs/prompts/reorder',
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompts: reorderData }),
        }
      );
      if (!response) {
        throw new Error('No response from server');
      }
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to reorder prompts');
      }
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-blurb-prompts'] });
      setSnackbar({
        open: true,
        message: 'Prompts reordered successfully',
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
      // Revert to server data on error
      queryClient.invalidateQueries({ queryKey: ['property-blurb-prompts'] });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    // Prevent reordering if already in progress
    if (reorderMutation.isPending) {
      return;
    }

    if (over && active.id !== over.id) {
      const oldIndex = prompts.findIndex((p) => p.id === active.id);
      const newIndex = prompts.findIndex((p) => p.id === over.id);

      const reorderedPrompts = arrayMove(prompts, oldIndex, newIndex);
      setPrompts(reorderedPrompts);
      reorderMutation.mutate(reorderedPrompts);
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      prompt: '',
      good_example: '',
      bad_example: '',
    });
  };

  const handleCreateClick = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const handleEditClick = (prompt: PropertyBlurbPrompt) => {
    setSelectedPrompt(prompt);
    setFormData({
      id: prompt.id,
      name: prompt.name,
      prompt: prompt.prompt,
      good_example: prompt.good_example || '',
      bad_example: prompt.bad_example || '',
    });
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (prompt: PropertyBlurbPrompt) => {
    setSelectedPrompt(prompt);
    setDeleteDialogOpen(true);
  };

  const handleCreateSubmit = () => {
    createMutation.mutate(formData);
  };

  const handleEditSubmit = () => {
    if (selectedPrompt) {
      updateMutation.mutate({
        id: selectedPrompt.id,
        data: {
          name: formData.name,
          prompt: formData.prompt,
          good_example: formData.good_example || undefined,
          bad_example: formData.bad_example || undefined,
        },
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedPrompt) {
      deleteMutation.mutate(selectedPrompt.id);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">Property Blurb Prompts</Typography>
        </Breadcrumbs>

        {/* Header with Action Button */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Property Blurb Prompts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage pre-defined prompts for generating property description blurbs using LLM
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
          >
            Create Prompt
          </Button>
        </Box>

        {/* Data Table */}
        <Box sx={{ position: 'relative' }}>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 400,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 50 }}></TableCell>
                      <TableCell sx={{ width: 90 }}>Order</TableCell>
                      <TableCell sx={{ minWidth: 250 }}>Prompt Name</TableCell>
                      <TableCell sx={{ width: 180 }}>ID</TableCell>
                      <TableCell sx={{ minWidth: 300 }}>Prompt Text</TableCell>
                      <TableCell sx={{ width: 150 }}>Created</TableCell>
                      <TableCell sx={{ width: 120 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <SortableContext
                      items={prompts.map((p) => p.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {prompts.map((prompt) => (
                        <SortableRow
                          key={prompt.id}
                          prompt={prompt}
                          onEdit={handleEditClick}
                          onDelete={handleDeleteClick}
                          disabled={reorderMutation.isPending}
                        />
                      ))}
                    </SortableContext>
                  </TableBody>
                </Table>
                {prompts.length === 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 200,
                      color: 'text.secondary',
                    }}
                  >
                    <Typography>No prompts found. Create your first prompt to get started.</Typography>
                  </Box>
                )}
              </DndContext>
            )}
          </TableContainer>

          {/* Reordering Overlay */}
          {reorderMutation.isPending && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 1000,
                borderRadius: 1,
              }}
            >
              <CircularProgress size={48} />
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                Saving new order...
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Property Blurb Prompt</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Prompt ID"
              value={formData.id}
              onChange={(e) => setFormData({ ...formData, id: e.target.value })}
              required
              fullWidth
              helperText="Unique identifier (e.g., 'surrounding_uses', 'property_highlights')"
            />
            <TextField
              label="Prompt Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Prompt Text"
              value={formData.prompt}
              onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
              required
              fullWidth
              multiline
              rows={6}
              helperText="The instruction/prompt that will be sent to the LLM"
            />
            <TextField
              label="Good Example (Optional)"
              value={formData.good_example}
              onChange={(e) => setFormData({ ...formData, good_example: e.target.value })}
              fullWidth
              multiline
              rows={4}
              helperText="Example of a good response to guide the LLM"
            />
            <TextField
              label="Bad Example (Optional)"
              value={formData.bad_example}
              onChange={(e) => setFormData({ ...formData, bad_example: e.target.value })}
              fullWidth
              multiline
              rows={4}
              helperText="Example of a bad response to avoid"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateSubmit}
            variant="contained"
            disabled={!formData.id || !formData.name || !formData.prompt || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Property Blurb Prompt</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Prompt ID"
              value={formData.id}
              disabled
              fullWidth
              helperText="ID cannot be changed"
            />
            <TextField
              label="Prompt Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Prompt Text"
              value={formData.prompt}
              onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
              required
              fullWidth
              multiline
              rows={6}
            />
            <TextField
              label="Good Example (Optional)"
              value={formData.good_example}
              onChange={(e) => setFormData({ ...formData, good_example: e.target.value })}
              fullWidth
              multiline
              rows={4}
            />
            <TextField
              label="Bad Example (Optional)"
              value={formData.bad_example}
              onChange={(e) => setFormData({ ...formData, bad_example: e.target.value })}
              fullWidth
              multiline
              rows={4}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={!formData.name || !formData.prompt || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Property Blurb Prompt</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the prompt "{selectedPrompt?.name}"?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. The prompt will be permanently deleted if it is not currently in use.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
